// src/components/common/Header.jsx
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { UserContext } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import ThemeToggle from './ThemeToggle';

const Header = ({ isLanding = false }) => {
  const { theme } = useContext(ThemeContext);
  const { user, userType } = useContext(UserContext);
  const { logout } = useAuth();
  const { refreshUser } = useUser();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className={`sticky top-0 z-50 ${theme === 'dark' ? 'dark' : ''}`}>
      <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">IntelliNote</span>
                <span className="ml-2 text-sm font-medium text-gray-600 dark:text-gray-300">AI</span>
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              {isLanding ? (
                <>
                  <Link to="/features" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Features
                  </Link>
                  <Link to="/pricing" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Pricing
                  </Link>
                  <Link to="/about" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    About
                  </Link>
                  <Link to="/login" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Login
                  </Link>
                  <Link to="/signup" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition">
                    Sign Up Free
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/dashboard" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Dashboard
                  </Link>
                  <Link to="/notes" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Notes
                  </Link>
                  <Link to="/flashcards" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    Flashcards
                  </Link>
                  <Link to="/ai-search" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    AI Search
                  </Link>
                  {userType === 'pro' && (
                    <span className="px-3 py-1 text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full">
                      PRO
                    </span>
                  )}
                </>
              )}
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              
              {!isLanding && user && (
                <div className="hidden md:flex items-center space-x-4">
                  <Link to="/profile" className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 dark:text-blue-300 font-bold">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </Link>

                  <button
                    onClick={async () => {
                      try {
                        await logout();
                      } catch (e) { /* ignore */ }
                      try { await refreshUser(); } catch (e) { /* ignore */ }
                      navigate('/');
                    }}
                    className="text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400"
                    aria-label="Sign out"
                  >
                    Sign out
                  </button>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-800">
              {isLanding ? (
                <div className="flex flex-col space-y-4">
                  <Link 
                    to="/features" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Features
                  </Link>
                  <Link 
                    to="/pricing" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link 
                    to="/about" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link 
                    to="/login" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/signup" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition mx-4"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign Up Free
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col space-y-4">
                  <Link 
                    to="/dashboard" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/notes" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Notes
                  </Link>
                  <Link 
                    to="/flashcards" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Flashcards
                  </Link>
                  <Link 
                    to="/ai-search" 
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    AI Search
                  </Link>
                  {user && (
                    <>
                      <Link 
                        to="/profile" 
                        className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-4 py-2 flex items-center"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                          <span className="text-blue-600 dark:text-blue-300 font-bold">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        Profile
                      </Link>
                      <button
                        onClick={async () => {
                          try {
                            await logout();
                          } catch (e) { /* ignore */ }
                          try { await refreshUser(); } catch (e) { /* ignore */ }
                          navigate('/');
                          setMobileMenuOpen(false);
                        }}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-500 px-4 py-2 text-left"
                      >
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;